export interface PropertySettings {
  has_push_notifications: boolean;
  has_schedule_restrictions: boolean;
  has_transactions_notifications: boolean;
  has_secondary_user_permissions_enabled: boolean;
  has_user_access_groups_permissions_enabled: boolean;
  has_security_guard_transactions_permissions_enabled: boolean;
  has_printable_pass_function: boolean;
  has_requirements_permissions: boolean;
}

export const DEFAULT_PROPERTY_SETTINGS: PropertySettings = {
  has_push_notifications: false,
  has_schedule_restrictions: false,
  has_transactions_notifications: false,
  has_secondary_user_permissions_enabled: true,
  has_user_access_groups_permissions_enabled: false,
  has_security_guard_transactions_permissions_enabled: true,
  has_printable_pass_function: false,
  has_requirements_permissions: false,
};

export interface SettingEntry {
  key: keyof PropertySettings;
  label: string;
  description: string;
  icon: string;
}

export interface SettingSection {
  title: string;
  items: SettingEntry[];
}

// Copy ported verbatim from the pre-refactor App.tsx (propertySettingsEntries),
// only grouped into sections to match the design's visual layout.
export const SETTING_SECTIONS: SettingSection[] = [
  {
    title: "Avisos y notificaciones",
    items: [
      {
        key: "has_push_notifications",
        label: "Notificaciones push",
        description:
          "Envía alertas push a los usuarios de la propiedad cuando ocurren eventos de acceso (función para app nativa).",
        icon: "bell",
      },
      {
        key: "has_transactions_notifications",
        label: "Notificacion vía whatsapp y/o correo electrónico",
        description:
          "Notifica los registros de acceso por WhatsApp y/o correo electrónico a los usuarios configurados.",
        icon: "mail",
      },
    ],
  },
  {
    title: "Operación de accesos",
    items: [
      {
        key: "has_schedule_restrictions",
        label: "Restricciones de horario de salida",
        description:
          "Permite configurar horarios permitidos para la salida de visitantes o residentes.",
        icon: "clock",
      },
      {
        key: "has_printable_pass_function",
        label: "Permitir imprimir etiquetas de acceso",
        description:
          "Habilita la impresión de etiquetas o pases de acceso desde la plataforma al generar un registro de acceso.",
        icon: "printer",
      },
      {
        key: "has_requirements_permissions",
        label: "Permisos para alta de campos personalizados",
        description:
          "Permite crear y administrar campos personalizados en el registro de visitantes o accesos.",
        icon: "clipboard",
      },
    ],
  },
  {
    title: "Permisos por rol",
    items: [
      {
        key: "has_secondary_user_permissions_enabled",
        label: "Permisos de usuario secundario",
        description:
          "Permite a los usuarios secundarios generar invitaciones y ver su bitácora de accesos de su unidad. Al estar deshabilitado, los usuarios secundarios solo podrán generar su llave de acceso personal.",
        icon: "users",
      },
      {
        key: "has_user_access_groups_permissions_enabled",
        label: "Permisos de grupos de acceso por unidad",
        description:
          "Permite a los usuarios principales configurar y administrar los accesos, horarios y reglas de los usuarios secundarios de la unidad.",
        icon: "users-round",
      },
      {
        key: "has_security_guard_transactions_permissions_enabled",
        label: "Permisos de registros manuales de guardia",
        description:
          "Permite a los guardias registrar manualmente entradas y salidas desde su panel.",
        icon: "shield-check",
      },
    ],
  },
];

export interface PropertyTypeOption {
  label: string;
  value: "Residential" | "Industrial" | "Corporate";
  icon: string;
  description: string;
}

// label/value pairs ported verbatim from the pre-refactor App.tsx (propertyTypes);
// icon/description are visual-only additions from the design reference.
export const PROPERTY_TYPES: PropertyTypeOption[] = [
  {
    label: "Residencial",
    value: "Residential",
    icon: "home",
    description: "Departamentos y casas con residentes y visitantes.",
  },
  {
    label: "Industrial",
    value: "Industrial",
    icon: "factory",
    description: "Naves, patios de maniobra y control de carga.",
  },
  {
    label: "Corporativo",
    value: "Corporate",
    icon: "building-2",
    description: "Oficinas por departamento, empleados y proveedores.",
  },
];

export interface LayoutPin {
  accessPointId: string;
  /** 0–100, relative to the displayed image width. */
  xPercent: number;
  /** 0–100, relative to the displayed image height. */
  yPercent: number;
}

/**
 * Serializable layout snapshot for later re-render elsewhere:
 * paint `image` and place each pin at (xPercent%, yPercent%) of the image.
 */
export interface PropertyLayoutData {
  image: string;
  pins: LayoutPin[];
}

export interface PropertyDetails {
  name: string;
  street: string;
  streetNumber: string;
  colonia: string;
  city: string;
  state: string;
  zip: string;
  address: string;
  type: PropertyTypeOption["value"] | "";
  tz: string;
  lat: string;
  lng: string;
  logo: string;
  layoutImage: string;
  /** Pins on the layout image, in % relative to the image (not the container). */
  layoutPins: LayoutPin[];
  settings: PropertySettings;
}

export const toPropertyLayoutData = (
  prop: Pick<PropertyDetails, "layoutImage" | "layoutPins">,
): PropertyLayoutData => ({
  image: prop.layoutImage,
  pins: prop.layoutPins ?? [],
});

export const DEFAULT_PROPERTY: PropertyDetails = {
  name: "",
  street: "",
  streetNumber: "",
  colonia: "",
  city: "",
  state: "",
  zip: "",
  address: "",
  type: "",
  tz: "",
  lat: "19.42604",
  lng: "-99.17153",
  logo: "",
  layoutImage: "",
  layoutPins: [],
  settings: DEFAULT_PROPERTY_SETTINGS,
};

export type GateType = "Entrada" | "Salida";

export interface Gate {
  id: string;
  name: string;
  type: GateType;
  strapiId?: number;
  documentId?: string;
}

export const makeEmptyGate = (
  name = "Puerta 1",
  type: GateType = "Entrada",
): Gate => ({
  id: crypto.randomUUID(),
  name,
  type,
});

// Same shape as the pre-refactor App.tsx's local `AccessPoint` interface —
// kept as-is (including the "longitud"/"latitud" naming) since it's what
// gets sent straight through to CreateAccessPointRequest.
export interface AccessPoint {
  id: string;
  nombre: string;
  calleOAvenida: string;
  longitud: string;
  latitud: string;
  zonaHoraria: string;
  gates: Gate[];
  strapiId?: number;
  documentId?: string;
}

export const makeEmptyAccessPoint = (
  coords?: { lat?: string; lng?: string },
): AccessPoint => ({
  id: crypto.randomUUID(),
  nombre: "",
  calleOAvenida: "",
  longitud: coords?.lng?.trim() ?? "",
  latitud: coords?.lat?.trim() ?? "",
  zonaHoraria: "",
  gates: [makeEmptyGate()],
});

// --- Grupos de acceso (etapa 3) ---------------------------------------------
// UI-only for now: no create-access-group endpoint exists yet in src/api,
// so this state is never sent to the backend (see useCreatePropertyWizard).
// Field names loosely follow AccessGroupsDataAttributes / ConfigurationAccessGroupsData
// (src/models/access-groups.ts) to make future wiring straightforward.

// `roleTypePrefix` mirrors INVITE_ROLES' — matched live against
// GET /api/users-permissions/roles via resolveRoleId, never hardcoded.
export const GROUP_ROLES = [
  { key: "admin", label: "Administración", icon: "shield", roleTypePrefix: "network_admin" },
  { key: "principal", label: "Usuarios Principales", icon: "user-round", roleTypePrefix: "host" },
  { key: "secundario", label: "Usuarios Secundarios", icon: "users-round", roleTypePrefix: "secondary_user" },
  { key: "visitante", label: "Visitantes", icon: "user-plus", roleTypePrefix: "visitor" },
] as const;

export type GroupRoleKey = (typeof GROUP_ROLES)[number]["key"];

// Keys match the real backend enum for ConfigurationAccessGroupsData.photos
// (confirmed against passtrack-backend-strapi's access-group lifecycle validator).
export const GROUP_PHOTOS = [
  { key: "id", label: "Identificación", icon: "id-card" },
  { key: "plates", label: "Placas del vehículo", icon: "car-front" },
  { key: "visitor", label: "Retrato de la persona", icon: "user-round" },
  { key: "other", label: "Documento adicional", icon: "file-text" },
] as const;

export type GroupPhotoKey = (typeof GROUP_PHOTOS)[number]["key"];

export const WEEK_DAYS = ["L", "M", "M", "J", "V", "S", "D"];

// Same index order as WEEK_DAYS (Mon..Sun) — real backend day-name strings
// expected inside ConfigurationAccessGroupsData.week_days.
export const WEEK_DAY_NAMES = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export type AccessGroupFieldType = "input" | "selector";

export const ACCESS_GROUP_FIELD_TYPES: {
  value: AccessGroupFieldType;
  label: string;
}[] = [
  { value: "input", label: "Input" },
  { value: "selector", label: "Selector" },
];

export interface AccessGroupField {
  id: string;
  label: string;
  type: AccessGroupFieldType;
  /** Comma-separated options when type is selector. */
  options: string;
  required: boolean;
}

export const makeEmptyAccessGroupField = (): AccessGroupField => ({
  id: crypto.randomUUID(),
  label: "",
  type: "input",
  options: "",
  required: false,
});

export interface AccessGroup {
  id: string;
  name: string;
  points: string[]; // AccessPoint.id[]
  roles: GroupRoleKey[];
  photos: Record<GroupPhotoKey, boolean>;
  from: string;
  to: string;
  exitRestricted: boolean;
  exitFrom: string;
  exitTo: string;
  days: number[]; // 7 entries, 1 = active
  fields: AccessGroupField[];
  strapiId?: number;
  documentId?: string;
}

export const makeEmptyAccessGroup = (name: string): AccessGroup => ({
  id: crypto.randomUUID(),
  name,
  points: [],
  roles: [],
  photos: { id: false, plates: false, visitor: false, other: false },
  from: "08:00",
  to: "20:00",
  exitRestricted: false,
  exitFrom: "08:00",
  exitTo: "20:00",
  days: [1, 1, 1, 1, 1, 0, 0],
  fields: [],
});

// --- Unidades (etapa 4) ------------------------------------------------------

export interface WizardUnit {
  id: string;
  name: string;
  locked: boolean; // true only for the always-created "Administración" unit
  strapiId?: number;
  documentId?: string;
}

export const ADMIN_UNIT_NAME = "Administración";

export const makeInitialUnits = (): WizardUnit[] => [
  { id: crypto.randomUUID(), name: ADMIN_UNIT_NAME, locked: true },
];

// --- Usuarios (etapa 5) ------------------------------------------------------

export const INVITE_ROLES = [
  {
    key: "Administración",
    label: "Administración",
    icon: "shield",
    hint: "Configura la propiedad completa.",
    needsUnit: false,
    needsPoints: false,
    // Strapi `users-permissions` role `type` prefix for this invite role,
    // live-verified against production (GET /api/users-permissions/roles):
    // "property_admin_a" -> type "network_admin", id 11.
    roleTypePrefix: "network_admin",
  },
  {
    key: "Usuario Principal",
    label: "Usuario Principal",
    icon: "user-round",
    hint: "Titular de una unidad.",
    needsUnit: true,
    needsPoints: false,
    // "host_a" -> type "host", id 9.
    roleTypePrefix: "host",
  },
  {
    key: "Guardia de Seguridad",
    label: "Guardia de Seguridad",
    icon: "shield-check",
    hint: "Opera puntos de acceso.",
    needsUnit: false,
    needsPoints: true,
    // "security_guard_a" -> type "security_guard", id 15.
    roleTypePrefix: "security_guard",
  },
] as const;

export type InviteRoleKey = (typeof INVITE_ROLES)[number]["key"];

// Roles aren't defined anywhere in backend code (confirmed: no seed/bootstrap
// script creates them, only hand-configured in the Strapi admin panel), and
// pt-strapi's own codebase shows role ids can drift/be inconsistent — so
// instead of hardcoding ids a second time, resolve them live from
// GET /api/users-permissions/roles by matching `type` against the prefix
// above. Plan-A roles (the active ones today) have a bare `type` like
// "host"/"security_guard"/"network_admin"; plan-B variants end in "_b" — pick
// the non-"_b" match first, fall back to a "_b" one only if that's all there
// is, and return null (never a guess) if nothing matches at all.
export const resolveRoleIdByPrefix = (
  roles: { id: number; type: string }[],
  prefix: string,
): number | null => {
  const matches = roles.filter(
    (r) => r.type === prefix || r.type.startsWith(`${prefix}_`),
  );
  const planA = matches.find((r) => !r.type.endsWith("_b"));
  return (planA ?? matches[0])?.id ?? null;
};

export const resolveInviteRoleId = (
  roles: { id: number; type: string }[],
  key: InviteRoleKey,
): number | null => {
  const prefix = INVITE_ROLES.find((r) => r.key === key)?.roleTypePrefix;
  return prefix ? resolveRoleIdByPrefix(roles, prefix) : null;
};

export const resolveGroupRoleId = (
  roles: { id: number; type: string }[],
  key: GroupRoleKey,
): number | null => {
  const prefix = GROUP_ROLES.find((r) => r.key === key)?.roleTypePrefix;
  return prefix ? resolveRoleIdByPrefix(roles, prefix) : null;
};

export interface WizardUser {
  id: string;
  name: string;
  email: string;
  phoneCountryCode: string;
  phoneNumber: string;
  role: InviteRoleKey;
  unit: string; // WizardUnit.name
  points: string[]; // AccessPoint.id[]
  locked: boolean; // true for the mandatory first admin entry
  strapiId?: number;
  documentId?: string;
}

export const PHONE_COUNTRY_CODES = ["+52", "+1"];

export const unitNameForInviteRole = (
  role: InviteRoleKey,
  currentUnit = "",
): string => {
  if (role === "Administración") return ADMIN_UNIT_NAME;
  const meta = INVITE_ROLES.find((r) => r.key === role);
  return meta?.needsUnit ? currentUnit : "";
};

export const makeEmptyUser = (
  role: InviteRoleKey = "Usuario Principal",
): WizardUser => ({
  id: crypto.randomUUID(),
  name: "",
  email: "",
  phoneCountryCode: "+52",
  phoneNumber: "",
  role,
  unit: unitNameForInviteRole(role),
  points: [],
  locked: false,
});

export const STEP_META = [
  {
    title: "Propiedad",
    hint: "Nombre, configuración y tipo",
    label: "Paso 1 de 5",
    desc: "Datos generales de la propiedad que estás dando de alta en la plataforma.",
  },
  {
    title: "Puntos de acceso",
    hint: "Ubicación y puertas",
    label: "Paso 2 de 5",
    desc: "Marca los puntos físicos donde se registrarán entradas y salidas, y define sus puertas.",
  },
  {
    title: "Grupos de acceso",
    hint: "Reglas por rol y punto",
    label: "Paso 3 de 5",
    desc: "Cada grupo cruza puntos de acceso con roles y define requisitos, horario y campos del registro.",
  },
  {
    title: "Unidades",
    hint: "Departamentos, casas o áreas",
    label: "Paso 4 de 5",
    desc: "Da de alta los espacios que conforman la propiedad; la unidad de Administración se crea automáticamente.",
  },
  {
    title: "Usuarios",
    hint: "Administrador e invitaciones",
    label: "Paso 5 de 5",
    desc: "Define el usuario administrador de la propiedad e invita a los primeros usuarios.",
  },
] as const;

export const TOTAL_STEPS = STEP_META.length;
